<template>
    <v-container fluid>
        <!-- Context Tiles Area -->
        <v-row dense>
            <!-- Chart / Insights -->
            <v-col cols="12" md="8">
                <v-card class="pa-4" outlined tile>
                    <v-card-title class="subtitle-1 font-weight-medium">
                        Insights (snapshot)
                    </v-card-title>

                    <v-card-text>
                        <canvas ref="insightChart" height="140"></canvas>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Contextual Tiles -->
            <v-col cols="12" md="4">
                <v-card class="pa-4" outlined tile>
                    <v-card-title class="subtitle-1 font-weight-medium">
                        Contextual tiles
                    </v-card-title>

                    <v-card-text>
                        <!-- Dynamic action buttons -->
                        <v-btn v-if="selected.documentType === 'leave'" outlined block color="primary" class="mb-2"
                            @click="loadContextTile('leaveBalance')">
                            Load leave balance
                        </v-btn>

                        <v-btn v-if="selected.documentType === 'requisition'" outlined block color="primary"
                            class="mb-2" @click="loadContextTile('budgetSnapshot')">
                            Load budget snapshot
                        </v-btn>

                        <v-btn v-if="selected.documentType === 'store'" outlined block color="primary" class="mb-2"
                            @click="loadContextTile('availability')">
                            Check item availability
                        </v-btn>

                        <v-btn text block color="secondary" class="mb-3" @click="loadOriginal">
                            <v-icon left>mdi-file-document-outline</v-icon>
                            Open original application
                        </v-btn>

                        <!-- Rendered Dynamic Tiles -->
                        <v-card v-if="context.leaveBalance" outlined class="pa-3 mb-2">
                            <div class="font-weight-medium mb-1">Leave balance</div>
                            <div class="text-body-2">
                                Remaining: {{ context.leaveBalance.remaining }} days
                            </div>
                        </v-card>

                        <v-card v-if="context.budgetSnapshot" outlined class="pa-3 mb-2">
                            <div class="font-weight-medium mb-1">Budget snapshot</div>
                            <div class="text-body-2">
                                Initial: {{ displayAmount(context.budgetSnapshot.initial) }}
                            </div>
                            <div class="text-body-2">
                                Used: {{ displayAmount(context.budgetSnapshot.used) }}
                            </div>
                            <div class="text-body-2">
                                Available: {{ displayAmount(context.budgetSnapshot.available) }}
                            </div>
                        </v-card>

                        <v-card v-if="context.availability" outlined class="pa-3 mb-2">
                            <div class="font-weight-medium mb-1">Stock</div>
                            <div class="text-body-2">
                                In stock: {{ context.availability.inStock }}
                            </div>
                            <div class="text-body-2">
                                Requested: {{ context.availability.requested }}
                            </div>
                        </v-card>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Original record preview -->
        <v-row class="mt-3">
            <v-col cols="12">
                <v-card flat>
                    <v-card-text>
                        <div v-if="loadingOriginal">
                            <v-progress-circular indeterminate color="primary" size="24" />
                            <span class="ml-2">Loading original document…</span>
                        </div>

                        <div v-else-if="originalRecord">
                            <!-- Placeholder for record preview -->
                            <v-alert type="info" outlined>
                                Preview of: {{ originalRecord.raw.documentType }}
                            </v-alert>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row class="mt-4 pt-3" style="border-top: 1px solid rgba(255,255,255,0.06)">
            <v-col class="text-right">
                <v-btn v-if="selected.status === 'pending'" outlined color="error" class="mr-2"
                    @click="onReject(selected.id)">
                    <v-icon left>mdi-block-helper</v-icon>
                    Reject
                </v-btn>

                <v-btn v-if="selected.status === 'pending'" color="success" class="mr-2" dark
                    @click="onApprove(selected.id)">
                    <v-icon left>mdi-check</v-icon>
                    Approve
                </v-btn>

                <v-btn text color="secondary" @click="mockComment">
                    Add comment
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import Chart from "chart.js";

export default {
    name: 'ContexualApprovalInformation',
    data() {
        return {
            selected: { documentType: "requisition", status: "pending", id: 42 },
            context: {},
            originalRecord: null,
            loadingOriginal: false,
        };
    },
    mounted() {
        this.renderChart();
    },
    methods: {
        renderChart() {
            const ctx = this.$refs.insightChart.getContext("2d");

            new Chart(ctx, {
                type: "line",
                data: {
                    labels: ["May", "Jun", "Jul", "Aug", "Sep", "Oct"],
                    datasets: [
                        {
                            label: "Value",
                            data: [120, 200, 150, 240, 180, 300],
                            borderColor: "#00b894",
                            borderWidth: 3,
                            fill: false,
                            lineTension: 0.4, // older syntax
                        },
                    ],
                },
                options: {
                    responsive: true,
                    legend: { display: false },
                    scales: {
                        xAxes: [{ ticks: { fontColor: "#9FB0BD" } }],
                        yAxes: [{ ticks: { fontColor: "#9FB0BD" } }],
                    },
                },
            });
        },
        displayAmount(v) {
            return new Intl.NumberFormat().format(v);
        },
        loadContextTile(type) {
            if (type === "leaveBalance") {
                this.context.leaveBalance = { remaining: 7 };
            } else if (type === "budgetSnapshot") {
                this.context.budgetSnapshot = {
                    initial: 10000,
                    used: 4600,
                    available: 5400,
                };
            } else if (type === "availability") {
                this.context.availability = { inStock: 32, requested: 5 };
            }
        },
        loadOriginal() {
            this.loadingOriginal = true;
            setTimeout(() => {
                this.originalRecord = { raw: { documentType: "Requisition" } };
                this.loadingOriginal = false;
            }, 1000);
        },
        onApprove(id) {
            alert(`Approved record #${id}`);
        },
        onReject(id) {
            alert(`Rejected record #${id}`);
        },
        mockComment() {
            alert("Comment flow (mock)");
        },
    },
};
</script>

<style scoped>
.v-card {
    border-radius: 16px;
    transition: 0.3s;
}

.v-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
